"use client";

import { Routes } from "@/api";
import {
  followUserAction,
  unfollowUserAction,
} from "@/app/actions/user/user-actions";
import { ChatChannelsQuery } from "@/hooks/queries/chat/use-channels-query";

import { IsUserFollowingQuery } from "@/hooks/queries/user/use-is-user-following";
import { MeQuery } from "@/hooks/queries/user/use-me-query";
import { UserFollowersQuery } from "@/hooks/queries/user/use-user-followers-query";
import { UserFollowingQuery } from "@/hooks/queries/user/use-user-following-query";
import { DefaultSession } from "next-auth";
import { useSession } from "next-auth/react";
import { redirect, RedirectType } from "next/navigation";
import React, { PropsWithChildren } from "react";

type FollowsContextType = {
  readonly isFollowing: boolean;
  readonly isFollowAllowed: boolean;
  readonly isSameUser: boolean;
  readonly followingId: string | undefined;
  readonly updateFollowing: () => void;
  readonly user: DefaultSession["user"] | null;
};

const FollowsContext = React.createContext<FollowsContextType | undefined>(
  undefined,
);

interface Props {
  portfolioId?: string;
  userId?: string;
  user?: FollowsContextType["user"];
}
export function FollowsProvider(props: PropsWithChildren<Props>) {
  const { data: session } = useSession();

  // No need to fetch portfolio in case we already have the userId
  // const portfolioQuery = PortfolioQuery.useQuery(props.portfolioId ?? "", {
  //   enabled: Boolean(props.portfolioId) && !props.userId,
  // });

  // const portfolio = portfolioQuery.data;

  const isFollowingQuery = IsUserFollowingQuery.useQuery(
    session?.user?.id ?? "",
    props.userId ?? "",
    {
      enabled:
        Boolean(session?.user?.id && props.userId) &&
        session?.user.id.localeCompare(props.userId!) !== 0,
    },
  );
  const isFollowing = Boolean(isFollowingQuery.data?.isFollowing);

  const updateFollowing = React.useCallback(async () => {
    const userId = props.userId;
    if (!session?.user?.id || !userId) {
      redirect(Routes.AUTHENTICATION, RedirectType.replace);
    }

    if (isFollowing) {
      await unfollowUserAction(session.user.id, userId);
    } else {
      await followUserAction(session.user.id, userId);
    }

    MeQuery.invalidate();
    IsUserFollowingQuery.invalidate(session.user.id, userId);
    UserFollowersQuery.invalidate();
    UserFollowingQuery.invalidate();
    ChatChannelsQuery.invalidate();
  }, [props.userId, session?.user.id, isFollowing]);

  return (
    <FollowsContext.Provider
      value={{
        updateFollowing,
        followingId: props.userId,
        isFollowing,
        isSameUser: session?.user?.id === props.userId,
        isFollowAllowed: true,
        user: props.user ?? null,
      }}
    >
      {props.children}
    </FollowsContext.Provider>
  );
}

export const useFollowsContext = () => {
  const context = React.useContext(FollowsContext);

  if (!context) {
    throw new Error("useFollowsContext must be used within a FollowsProvider");
  }

  return context;
};
